import React, { useState } from "react";
import { useInView } from "react-intersection-observer";
import PropTypes from "prop-types";
import useTimeout from "../../hooks/use-timeout.js";

import styles from "./vimeo-video-frame.module.scss";

/**
 * This component renders the video frame from Vimeo.
 *
 * @param {Object} props - The object containing props.
 * @param {string} props.id - optional string if we want to change the default one "video-iframe" applied to the `<iframe>`.
 * @param {string} props.vimeoId - required string number got from Vimeo file to use it at src attribute of `<iframe>`.
 * @note TODO: Remove videoTitle attribute and use just title as they are used for the same purpose.
 * @param {string} props.videoTitle - used in 2 places, the `<iframe>` title and if showTitle is true we use it in the `<h5>`.
 * @param {string} props.title - shown just when videoTitle is not defined and used in 2 places, the `<iframe>` title and if showTitle is true we use it in the `<h5>`.
 * @param {boolean} props.showTitle - boolean value to show or hide an `<h5>` element below the video `<iframe>`.
 * @param {string} props.class - optional classes applied to the className attribute from `<iframe>`.
 * @param {string} props.classTitle - optional classes applied to the className attribute from `<h5>`.
 * @param {string} props.allowFeatures - `<iframe>` features attribute like "autoplay; fullscreen" or even all available for the `<iframe>`.
 * @param {number} props.muted - numeric value like 0 or 1 to use in the url `?muted=${muted}` to get the exact video from Vimeo.
 * @param {number} props.autoplay - numeric value like 0 or 1 to use in the url `?autoplay=${autoplay}` to get the exact video from Vimeo.
 * @param {number} props.loop - numeric value like 0 or 1 to use in the url `?loop=${loop}` to get the exact video from Vimeo.
 * @param {string} props.hParam - string value used after the loop url param `?loop=${loop}` + hParam.
 * @param {string} props.maxWidth - used for the style of the `<iframe>` style={{ maxWidth: maxWidth }}.
 * @returns {React.JSX.Element} Returns the JSX component to render.
 */
const VimeoVideoFrame = ({
  id = "video-iframe",
  vimeoId = "",
  videoTitle = "",
  title = "",
  showTitle = false,
  class: className = "m-auto iframe w-100 border-radius-12",
  classTitle = "",
  allowFeatures = "fullscreen", // "autoplay; fullscreen",
  muted = 0,
  autoplay = 0,
  loop = 0,
  hParam = "",
  maxWidth = ""
}) => {
  const [hasTimeElapsed, setHasTimeElapsed] = useState(false);
  useTimeout(() => {
    setHasTimeElapsed(true);
  }, 3000);
  let vimeoIframeRef, isVimeoIframeInView;
  if (typeof window !== "undefined" && typeof window.IntersectionObserver !== "undefined") {
    [vimeoIframeRef, isVimeoIframeInView] = useInView({
      triggerOnce: true
    });
  } else if (typeof window !== "undefined" && typeof window.IntersectionObserver === "undefined") {
    isVimeoIframeInView = true;
  }

  return (
    <>
      <iframe
        id={id}
        ref={vimeoIframeRef}
        src={
          isVimeoIframeInView && hasTimeElapsed
            ? `https://player.vimeo.com/video/` + vimeoId + `?muted=${muted}&autoplay=${autoplay}&loop=${loop}` + hParam
            : null
        }
        frameBorder="0"
        allow={allowFeatures}
        className={`${styles.vimeoVideoFrame} ${className}`}
        title={videoTitle || title}
        style={{ maxWidth: maxWidth }}
      />
      {showTitle && <h5 className={`text-green-60 mt-1 ${classTitle}`}>{videoTitle || title}</h5>}
    </>
  );
};
export default VimeoVideoFrame;

VimeoVideoFrame.propTypes = {
  id: PropTypes.string,
  vimeoId: PropTypes.string.isRequired,
  videoTitle: PropTypes.string,
  title: PropTypes.string,
  showTitle: PropTypes.bool,
  class: PropTypes.string,
  classTitle: PropTypes.string,
  allowFeatures: PropTypes.string, // example: "autoplay; fullscreen"
  muted: PropTypes.number,
  autoplay: PropTypes.number,
  loop: PropTypes.number,
  hParam: PropTypes.string,
  maxWidth: PropTypes.string
};
